import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { ArrowLeft, ShieldCheck, Clock, CreditCard, MessageCircle } from "lucide-react";

// ---------------------------------------------------------------------------
// Refund & Cancellation Policy — required for Indian
// payment processors (originally drafted for Cashfree; now applies to Cashfree, our
// active gateway). Word-for-word from the policy we
// described to the gateway during onboarding.
// platforms. All numbers / timelines below match the operating model
// described to Cashfree during onboarding.
// ---------------------------------------------------------------------------
export default function Refund() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4"/> Back to home
        </Link>
        <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-semibold text-slate-900">
          Refund &amp; Cancellation Policy
        </h1>
        <p className="mt-2 text-sm text-slate-500">Last updated: 22 February 2026</p>

        <Card className="mt-8 p-6 sm:p-8 border-slate-200 space-y-8">
          <Section icon={ShieldCheck} title="100% money-back guarantee">
            If a verified specialist on NeuroOpinion is unable to accept your case
            within 24 hours of payment, the entire amount is refunded automatically
            to the original payment instrument — no support ticket needed.
          </Section>

          <Section icon={Clock} title="Cancellation by patient — before doctor accepts">
            You may cancel a paid consultation at any time before a doctor accepts
            your case. We process a <b>full refund</b> via Cashfree to your card,
            UPI ID, or wallet.
          </Section>

          <Section icon={Clock} title="Cancellation by patient — after doctor accepts">
            Once a doctor has accepted and started reviewing your case, the
            consultation cannot be cancelled, as the specialist&apos;s time has
            already been committed. Refund is not applicable in this case unless
            the doctor explicitly fails to deliver a written report within 48
            hours of accepting (Regular tier) / 24 hours (Priority or Video Consultation tier).
          </Section>

          <Section icon={CreditCard} title="Refund processing time">
            Refunds initiated by NeuroOpinion typically reflect in your account
            within <b>5–7 business days</b>, depending on your bank or card issuer.
            Cashfree sends a confirmation SMS / email when the refund is issued.
          </Section>

          <Section icon={MessageCircle} title="Disputes &amp; partial refunds">
            If you are not satisfied with the second opinion you received, please
            raise a concern within 7 days at <b>support@neuroopinion.in</b> or
            on WhatsApp <b>+91 83176 74160</b>. We will review the case fairly
            and may issue a partial or full refund at our discretion, based on
            the medical merit of the complaint.
          </Section>

          <Section icon={ShieldCheck} title="What is non-refundable">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Cases where the patient has provided false / incomplete medical history</li>
              <li>Cases marked &quot;completed&quot; by the doctor with a delivered written report</li>
              <li>Convenience charges &amp; payment gateway fees (typically &lt;2% of fee)</li>
            </ul>
          </Section>
        </Card>

        <p className="mt-6 text-xs text-slate-500">
          For any question on refunds, contact <a className="text-[#0B5ED7] font-medium hover:underline" href="mailto:support@neuroopinion.in">support@neuroopinion.in</a> or WhatsApp <a className="text-[#0B5ED7] font-medium hover:underline" href="https://wa.me/918317674160">+91 83176 74160</a>.
        </p>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="inline-flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-[#0B5ED7]/10 text-[#0B5ED7] grid place-items-center">
          <Icon className="w-4 h-4"/>
        </div>
        <h2 className="font-heading font-semibold text-slate-900 text-lg">{title}</h2>
      </div>
      <div className="mt-3 text-sm text-slate-700 leading-relaxed pl-10">
        {children}
      </div>
    </div>
  );
}
